import {
  Box,
  Flex,
  Text,
  Skeleton,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { ViewSwitcher, splitTime } from '@devd-client/devd/components';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { RxReload } from 'react-icons/rx';
import { getAiVsNonAiMetric } from '../../graphql/query';
import CustomTooltip from './CustomTooltip';

const AI_KEY = 'AI_USER';
const NON_AI_KEY = 'NON_AI_USER';

const VIEW_SWITCH_OBJ = [
  { tab: 'Day', tooltipText: 'Day', TabIcon: '' },
  { tab: 'Week', tooltipText: 'Week', TabIcon: '' },
  { tab: 'Month', tooltipText: 'Month', TabIcon: '' },
];

const CARD_STYLE = {
  bg: 'white',
  borderRadius: '4px',
  border: '0.5px solid',
  borderColor: 'rgba(0, 0, 0, 0.1)',
  p: 4,
  minH: '400px',
  minW: '320px',
};

const formatNumber = (value: number): string => {
  if (isNaN(value) || value === 0) return '0';
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(1) + 'K';
  return value % 1 === 0 ? value.toString() : value.toFixed(1);
};

const formatValue = (
  value: number | null | undefined,
  label: string
): string => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  if (label === 'Minute' || label === 'Minutes') return splitTime(value / 60);
  if (label === 'Average Line Change') return formatNumber(value);
  return value.toLocaleString();
};

interface ChartDataPoint {
  name: string;
  [key: string]: string | number;
}

interface MetricMeta {
  title: string;
  infoText: string | null;
  averageLabel: string | null;
  yAxisLabel: string;
  aiLabel: string;
  nonAiLabel: string;
  average: string | null;
  improvement: string | null;
  aiAverage: number | null;
  nonAiAverage: number | null;
  aiColor: string;
  nonAiColor: string;
}

const DEFAULT_META: MetricMeta = {
  title: '',
  infoText: null,
  averageLabel: null,
  yAxisLabel: 'Count',
  aiLabel: 'AI User',
  nonAiLabel: 'Non AI User',
  average: null,
  improvement: null,
  aiAverage: null,
  nonAiAverage: null,
  aiColor: '#1D4ED8',
  nonAiColor: '#BFDBFE',
};

interface AiImpactMetricCardProps {
  metricKey: string;
  teamId: string;
  startDate: string;
  endDate: string;
  sprintId: string;
  breakdown?: string;
  onViewChange?: (view: string) => void;
}

export const AiImpactMetricCard: FC<AiImpactMetricCardProps> = ({
  metricKey,
  teamId,
  startDate,
  endDate,
  sprintId,
  breakdown = '',
  onViewChange,
}) => {
  const [viewToggle, setViewToggle] = useState('Week');
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<MetricMeta>(DEFAULT_META);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setIsLoading(true);
    setError(null);

    const controller = new AbortController();

    try {
      const response = await getAiVsNonAiMetric(
        metricKey,
        breakdown,
        viewToggle.toUpperCase(),
        teamId,
        startDate,
        endDate,
        sprintId,
        []
      );

      const metricData = response?.CompareMetricByAiVsNonAiUsers;
      if (!metricData) {
        setError('Unable to load metric data');
        setChartData([]);
        return;
      }

      if (metricData.chartError) {
        const isConfigError = metricData.chartError.message
          ?.toLowerCase()
          .includes('not configured');
        setError(
          isConfigError ? 'Data source not configured' : 'Unable to load data'
        );
        setChartData([]);
        return;
      }

      let parsedData: ChartDataPoint[] = [];
      try {
        parsedData = metricData.data ? JSON.parse(metricData.data) : [];
      } catch {
        if (process.env['NODE_ENV'] !== 'production') {
          console.error(
            `[AiImpactMetricCard] Failed to parse chart data for ${metricKey}`
          );
        }
        setError('Received malformed data from server');
        setChartData([]);
        return;
      }

      setChartData(
        parsedData.map((item) => ({
          name: item['xLabelName'] as string,
          [AI_KEY]: item[AI_KEY],
          [NON_AI_KEY]: item[NON_AI_KEY],
        }))
      );

      const cm = metricData.chartMetadata;
      const aiMeta = metricData.keys?.find((k) => k.key === AI_KEY);
      const nonAiMeta = metricData.keys?.find((k) => k.key === NON_AI_KEY);

      setMeta({
        title: cm?.chartTitle ?? '',
        infoText: cm?.info ?? null,
        yAxisLabel: cm?.ylabel ?? 'Count',
        improvement: cm?.improvement ?? null,
        averageLabel: metricData.averageLabel ?? null,
        average:
          metricData.average !== null && metricData.average !== undefined
            ? metricData.average
            : null,
        aiLabel: aiMeta?.name ?? 'AI User',
        aiColor: aiMeta?.color ?? '#1D4ED8',
        aiAverage:
          typeof aiMeta?.avgValue === 'number' ? aiMeta.avgValue : null,
        nonAiLabel: nonAiMeta?.name ?? 'Non AI User',
        nonAiColor: nonAiMeta?.color ?? '#BFDBFE',
        nonAiAverage:
          typeof nonAiMeta?.avgValue === 'number' ? nonAiMeta.avgValue : null,
      });
    } catch (err) {
      if (controller.signal.aborted) return;

      let errorMsg = 'Unable to fetch metric data';
      if (err instanceof Error) {
        if (err.message.toLowerCase().includes('network'))
          errorMsg = 'Network error. Please check your connection';
        else if (err.message.toLowerCase().includes('timeout'))
          errorMsg = 'Request timed out. Please try again';
        else if (err.message.toLowerCase().includes('not configured'))
          errorMsg = 'Data source not configured';
      }

      if (process.env['NODE_ENV'] !== 'production') {
        console.error(`[AiImpactMetricCard] Error fetching ${metricKey}:`, err);
      }

      setError(errorMsg);
      setChartData([]);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }

    return () => controller.abort();
  }, [viewToggle, metricKey, teamId, startDate, endDate, sprintId, breakdown]);

  useEffect(() => {
    const cleanup = fetchData();
    return () => {
      cleanup?.then((abort) => abort?.());
    };
  }, [fetchData]);

  const handleViewSwitch = useCallback(
    (view: string) => {
      setViewToggle(view);
      onViewChange?.(view);
    },
    [onViewChange]
  );

  const insightProps = useMemo(() => {
    const { average, improvement } = meta;
    if (!average || average === '' || isNaN(parseFloat(average))) {
      return null;
    }

    const pct = parseFloat(average);
    const isZero = pct === 0;

    if (isZero) {
      return {
        isZero: true,
        absPct: '0',
        directionWord: 'same',
      };
    }

    const absPct = Math.abs(pct).toFixed(1);
    const directionWord = pct < 0 ? 'fewer' : 'more';

    return {
      isZero: false,
      absPct,
      directionWord,
    };
  }, [meta]);

  const displayAiAverage =
    meta.aiAverage !== null && !isNaN(meta.aiAverage) ? meta.aiAverage : 0;
  const displayNonAiAverage =
    meta.nonAiAverage !== null && !isNaN(meta.nonAiAverage)
      ? meta.nonAiAverage
      : 0;

  const renderHeader = () => (
    <Box mb={1} width="100%">
      <Flex align="center" justify="space-between" mb={2}>
        <Flex direction="column" gap={0.5}>
          <Flex align="center" gap={2}>
            <Text
              fontSize="md"
              fontFamily="heading"
              fontWeight="semibold"
              color="text.primary"
            >
              {meta.title}
            </Text>
            {meta.infoText && (
              <Tooltip
                label={meta.infoText}
                hasArrow
                placement="top"
                bg="gray.700"
                color="white"
                fontSize="sm"
              >
                <Box cursor="pointer">
                  <AiOutlineInfoCircle color="#666" fontSize={16} />
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        <Flex align="center" gap={2}>
          <Tooltip
            hasArrow
            aria-label="reload-metric"
            label="Reload Metric Data"
          >
            <IconButton
              isRound
              size="sm"
              bg="none"
              aria-label="reload metric"
              icon={<RxReload size="16px" />}
              onClick={fetchData}
              isLoading={isLoading}
            />
          </Tooltip>
          <ViewSwitcher
            tabList={VIEW_SWITCH_OBJ}
            currentTab={viewToggle}
            handleSwitch={handleViewSwitch}
            height="25px"
          />
        </Flex>
      </Flex>
    </Box>
  );

  if (isLoading) {
    return (
      <Box {...CARD_STYLE}>
        <Skeleton height="24px" width="60%" mb={4} />
        <Skeleton height="40px" width="40%" mb={2} />
        <Skeleton height="300px" />
      </Box>
    );
  }

  if (error) {
    const isConfigError = error.toLowerCase().includes('not configured');
    return (
      <Box {...CARD_STYLE} display="flex" flexDirection="column">
        {renderHeader()}
        <Flex
          align="center"
          justify="center"
          flex={1}
          direction="column"
          gap={4}
        >
          <Box
            p={4}
            borderRadius="8px"
            bg="rgba(255, 0, 0, 0.05)"
            border="1px solid"
            borderColor="rgba(255, 0, 0, 0.2)"
            textAlign="center"
          >
            <Text
              color="#B10707"
              fontSize="sm"
              fontFamily="heading"
              fontWeight="medium"
            >
              {isConfigError ? 'Data not configured' : 'Unable to load data'}
            </Text>
            <Text
              color="text.secondary"
              fontSize="xs"
              fontFamily="heading"
              mt={1}
            >
              Please try again or contact support if the issue persists
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box {...CARD_STYLE} display="flex" flexDirection="column">
        {renderHeader()}
        <Flex
          align="center"
          justify="center"
          flex={1}
          direction="column"
          gap={2}
        >
          <Text
            color="text.secondary"
            fontSize="sm"
            fontFamily="heading"
            fontWeight="normal"
          >
            No data available for the selected period
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      {...CARD_STYLE}
      display="flex"
      flexDirection="column"
      position="relative"
    >
      {renderHeader()}

      <Flex align="flex-start" justify="space-between" gap={4}>
        <Flex align="flex-start" direction="column" gap={1.5}>
          <Flex align="flex-start" gap={10}>
            <Flex direction="column" gap={0.5}>
              <Flex align="center" gap={1.5}>
                <Box
                  w="10px"
                  h="10px"
                  borderRadius="2px"
                  bg={meta.aiColor}
                  flexShrink={0}
                />
                <Text fontSize="xs" color="text.secondary" fontFamily="heading">
                  {meta.aiLabel}
                </Text>
              </Flex>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="text.primary"
                fontFamily="heading"
              >
                {formatValue(displayAiAverage, meta.yAxisLabel)}
              </Text>
            </Flex>

            <Flex direction="column" gap={0.5}>
              <Flex align="center" gap={1.5}>
                <Box
                  w="10px"
                  h="10px"
                  borderRadius="2px"
                  bg={meta.nonAiColor}
                  border="1px solid"
                  borderColor="rgba(0,0,0,0.12)"
                  flexShrink={0}
                />
                <Text fontSize="xs" color="text.secondary" fontFamily="heading">
                  {meta.nonAiLabel}
                </Text>
              </Flex>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="text.primary"
                fontFamily="heading"
              >
                {formatValue(displayNonAiAverage, meta.yAxisLabel)}
              </Text>
            </Flex>
          </Flex>

          {meta.averageLabel && (
            <Flex align="center" gap={1.5}>
              <Text
                fontSize="xs"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="normal"
              >
                {meta.averageLabel}
              </Text>
            </Flex>
          )}
        </Flex>

        {insightProps && (
          <Box
            borderLeft="2px solid"
            borderColor="#E5E7EB"
            pl={3}
            maxW="200px"
            flexShrink={0}
            alignSelf="flex-end"
          >
            <Text
              fontSize="xs"
              color="#6B7280"
              fontFamily="heading"
              lineHeight="1.6"
            >
              {insightProps.isZero ? (
                <>AI users have same {meta.title} as non-AI users</>
              ) : (
                <>
                  AI users have{' '}
                  <Text as="span" fontWeight="700">
                    {insightProps.absPct}% {insightProps.directionWord}
                  </Text>{' '}
                  {meta.title} vs non-AI users
                </>
              )}
            </Text>
          </Box>
        )}
      </Flex>

      <Box flex={1} minH="250px" mt={6}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#E0E0E0"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              stroke="#999"
              fontSize={11}
              tick={{ fill: '#666' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke="#999"
              fontSize={11}
              tick={{ fill: '#666' }}
              axisLine={false}
              tickLine={false}
              label={{
                value: meta.yAxisLabel,
                angle: -90,
                position: 'insideLeft',
                style: { fill: '#666', fontSize: 11 },
              }}
              tickFormatter={(value) => formatValue(value, meta.yAxisLabel)}
            />
            <RechartsTooltip
              content={
                <CustomTooltip
                  yAxisLabel={meta.yAxisLabel}
                  formatValue={formatValue}
                />
              }
              cursor={{ fill: 'transparent' }}
              wrapperStyle={{ outline: 'none' }}
            />
            <Bar
              dataKey={AI_KEY}
              fill={meta.aiColor}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
              name={meta.aiLabel}
            />
            <Bar
              dataKey={NON_AI_KEY}
              fill={meta.nonAiColor}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
              name={meta.nonAiLabel}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default AiImpactMetricCard;
